import React, { useState, useRef, useMemo, useEffect } from 'react';
import { IStyleSettingColors } from 'types';
import Text from 'components/Text/Text';
import ButtonBlock from './components/ButtonBlock';
import StyleInput from './components/StyleInput';
import Navigation from './components/Navigation';
import AdditionalColors from './components/AdditionalColors';
import classNames from 'classnames';
import style from './StyleFields.module.scss';

interface IStyleFields {
	selectedGroup: {
		id: string;
		key: string;
	} | null;
	setStyleSettings(styleItemId: string, value: string, key: string): void;
	styleSettingColors: IStyleSettingColors;
	useCustomerSettings?: boolean;
	useForFullInfo?: boolean;
}

const StyleFields: React.FC<IStyleFields> = ({
	selectedGroup,
	setStyleSettings,
	styleSettingColors,
	useCustomerSettings = false,
	useForFullInfo = false,
}) => {
	const [activeColors, setActiveColors] = useState<'general' | 'additional'>('general');
	const [selectedNavigationItem, setSelectedNavigationItem] = useState<string | null>(
		styleSettingColors.additionalColors && Object.keys(styleSettingColors.additionalColors)[0],
	);

	const overlayClasses = classNames({ [style.disabled]: useCustomerSettings && !useForFullInfo });
	const generalColorsClasses = classNames(style.generalColors, { [style.hidden]: activeColors !== 'general' });
	const additionalColorsClasses = classNames(style.additionalColors, {
		[style.hidden]: activeColors !== 'additional',
	});
	const headingElement = useRef<null | HTMLDivElement>(null);

	const navigationItems = useMemo(() => {
		if (styleSettingColors.additionalColors) {
			return Object.keys(styleSettingColors.additionalColors);
		}
		return [];
	}, [styleSettingColors.additionalColors]);

	useEffect(() => {
		if (selectedGroup?.id === 'colorsID') {
			headingElement.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [selectedGroup]);

	return (
		<div className={style.container} ref={headingElement} data-test="StyleFields">
			<Text
				translationNameSpace="mediaSettings"
				translationKey="colors"
				theme="secondary"
				size="l"
				weight="bold"
			/>
			<ButtonBlock setActiveColors={setActiveColors} activeColors={activeColors} />
			<div className={overlayClasses}>
				<div className={generalColorsClasses}>
					{styleSettingColors.generalColors.map((el) => (
						<StyleInput
							setStyleSettings={setStyleSettings}
							data={el}
							key={el.id}
							isDisabled={useCustomerSettings || useForFullInfo}
						/>
					))}
				</div>
				<div className={additionalColorsClasses}>
					<Navigation
						navigationItems={navigationItems}
						selectNavigationItem={setSelectedNavigationItem}
						selectedNavigationItem={selectedNavigationItem}
					/>
					<AdditionalColors
						setStyleSettings={setStyleSettings}
						navigationItems={navigationItems}
						additionalColors={styleSettingColors.additionalColors}
						selectedNavigationItem={selectedNavigationItem}
						useCustomerSettings={useCustomerSettings || useForFullInfo}
					/>
				</div>
			</div>
		</div>
	);
};

export default React.memo(StyleFields);
